import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { MaintenanceExpenseHistoryService } from 'src/app/services/maintenance-expense-history.service';
import { UnitIntelligenceService } from 'src/app/services/unit-intelligence.service';
import { UnitService } from 'src/app/services/unit.service';
import { UtilsService } from 'src/app/services/utils.service';
import { QuotesService } from 'src/app/services/quotes.service';
import { MaintenanceService } from 'src/app/services/maintenance.service';
import { formatCurrency, formatKm, formatDate, formatDateLong } from '../unit-intelligence/shared/format.util';
import { LineChartSeries } from '../unit-intelligence/shared/line-chart/line-chart.component';

@Component({
    selector: 'app-maintenance-expense-history',
    templateUrl: './maintenance-expense-history.component.html',
    styleUrls: ['./maintenance-expense-history.component.css']
})
export class MaintenanceExpenseHistoryComponent implements OnInit {

    readonly Math = Math;
    readonly formatCurrency = formatCurrency;
    readonly formatKm = formatKm;
    readonly formatDate = formatDate;
    readonly formatDateLong = formatDateLong;

    filterFrom = '';
    filterTo = '';
    filterCategory = '';
    filterSupplierId = '';

    units: any[] = [];
    filteredUnits: any[] = [];
    unitSearchTerm = '';
    showUnitDropdown = false;
    selectedUnit: any = null;

    suppliers: any[] = [];
    failures: any[] = [];

    // Vista global
    data: any = null;
    loading = false;
    currentPage = 1;
    perPage = 10;
    tableSearch = '';
    tableFailureId = '';
    tableUnitId = '';
    evolutionSeries: LineChartSeries[] = [];
    evolutionLabels: string[] = [];
    monthlySeries: LineChartSeries[] = [];
    monthlyLabels: string[] = [];

    // Vista con unidad seleccionada (State B)
    unitData: any = null;
    unitLoading = false;
    unitCurrentPage = 1;
    unitPerPage = 10;
    historyTab: 'Todos' | 'Preventivo' | 'Correctivo' = 'Todos';
    unitHistorySearch = '';
    unitPageSizeOptions = [10, 25, 50];
    laborExpanded = false;
    partsExpanded = false;
    proveedoresExpanded = false;

    constructor(
        private svc: MaintenanceExpenseHistoryService,
        private unitIntelligence: UnitIntelligenceService,
        private unitService: UnitService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService,
        private quotesService: QuotesService,
        private maintenanceService: MaintenanceService
    ) { }

    ngOnInit(): void {
        const today = new Date();
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.filterFrom = firstOfMonth.toISOString().slice(0, 10);
        this.filterTo = today.toISOString().slice(0, 10);

        this.unitService.getInfo().subscribe({
            next: (resp: any) => {
                this.units = Array.isArray(resp) ? resp : (resp?.data || []);
            }
        });

        this.quotesService.getSuppliers('').subscribe({
            next: (resp: any) => {
                this.suppliers = Array.isArray(resp) ? resp : (resp?.data || []);
            }
        });

        this.maintenanceService.getData().subscribe({
            next: (resp: any) => {
                this.failures = Array.isArray(resp) ? resp : (resp?.data || []);
            }
        });

        this.fetchGlobal();
    }

    // --- Autocomplete de unidad ---

    onUnitSearch(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.unitSearchTerm = input.value;
        const term = this.unitSearchTerm.trim().toLowerCase();
        if (!term) {
            this.filteredUnits = [];
            this.showUnitDropdown = false;
            return;
        }
        this.filteredUnits = this.units
            .filter(u => (u.unit || '').toLowerCase().includes(term)
                || (u.plates || '').toLowerCase().includes(term)
                || (u.numero_motor || '').toLowerCase().includes(term))
            .slice(0, 10);
        this.showUnitDropdown = this.filteredUnits.length > 0;
    }

    hideUnitDropdown(): void {
        setTimeout(() => { this.showUnitDropdown = false; }, 150);
    }

    selectUnit(unit: any): void {
        this.selectedUnit = unit;
        this.unitSearchTerm = unit.unit;
        this.filteredUnits = [];
        this.showUnitDropdown = false;
        this.unitCurrentPage = 1;
        this.historyTab = 'Todos';
        this.laborExpanded = false;
        this.partsExpanded = false;
        this.proveedoresExpanded = false;
        this.fetchUnit();
    }

    clearFilters(): void {
        this.selectedUnit = null;
        this.unitSearchTerm = '';
        this.filterCategory = '';
        this.filterSupplierId = '';
        this.tableSearch = '';
        this.tableFailureId = '';
        this.tableUnitId = '';
        const today = new Date();
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.filterFrom = firstOfMonth.toISOString().slice(0, 10);
        this.filterTo = today.toISOString().slice(0, 10);
        this.currentPage = 1;
        this.fetchGlobal();
    }

    // --- Vista global ---

    fetchGlobal(): void {
        this.loading = true;
        this.spinner.show();
        const params: any = {
            from: this.filterFrom,
            to: this.filterTo,
            category: this.filterCategory,
            supplier_id: this.filterSupplierId,
            maintenance_failure_id: this.tableFailureId,
            table_unit_id: this.tableUnitId,
            search: this.tableSearch,
            page: this.currentPage,
            per_page: this.perPage,
        };

        this.svc.getExpenseHistory(params).subscribe({
            next: (resp: any) => {
                this.data = resp;
                this.buildCharts(resp);
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el histórico de gastos de mantenimiento', icon: 'error' });
            }
        });
    }

    applyFilters(): void {
        this.currentPage = 1;
        this.fetchGlobal();
    }

    /** Series/labels de los charts se calculan UNA vez por respuesta, no como
     * getters — un getter enlazado a [series]/[labels] devuelve un arreglo
     * nuevo en cada ciclo de detección de cambios, lo que dispara ngOnChanges
     * del chart en cada ciclo y puede trabar la pestaña. */
    private buildCharts(resp: any): void {
        const s = resp?.daily_series;
        this.evolutionSeries = s ? [
            { name: 'Total', color: '#00a63e', data: s.total },
            { name: 'Refacciones (Stock)', color: '#1447e6', data: s.refacciones_stock },
            { name: 'Preventivo', color: '#7f43d7', data: s.preventivo },
            { name: 'Refacciones (Solicitadas)', color: '#fb8b25', data: s.refacciones_solicitadas },
        ] : [];
        this.evolutionLabels = (s?.labels || []).map((d: string) => formatDate(d));

        const trend = resp?.monthly_trend;
        this.monthlySeries = trend ? [{ name: 'Total', color: '#00a63e', data: trend.total }] : [];
        this.monthlyLabels = trend?.labels || [];
    }

    goToPage(page: number): void {
        if (page < 1 || (this.data?.requests_table?.pagination?.last_page && page > this.data.requests_table.pagination.last_page)) return;
        this.currentPage = page;
        this.fetchGlobal();
    }

    get pageNumbers(): number[] {
        const lastPage = this.data?.requests_table?.pagination?.last_page || 1;
        const windowSize = 3;
        const end = Math.min(lastPage, Math.max(1, this.currentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    // --- Vista con unidad seleccionada ---

    fetchUnit(): void {
        if (!this.selectedUnit) return;
        this.unitLoading = true;
        this.spinner.show();
        const params: any = {
            from: this.filterFrom,
            to: this.filterTo,
            category: this.historyTab === 'Todos' ? '' : this.historyTab,
            search: this.unitHistorySearch,
            page: this.unitCurrentPage,
            per_page: this.unitPerPage,
        };

        this.unitIntelligence.getMaintenanceHistory(this.selectedUnit.id, params).subscribe({
            next: (resp: any) => {
                this.unitData = resp;
                this.unitLoading = false;
                this.spinner.hide();
            },
            error: () => {
                this.unitLoading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el histórico de la unidad', icon: 'error' });
            }
        });
    }

    setHistoryTab(tab: 'Todos' | 'Preventivo' | 'Correctivo'): void {
        this.historyTab = tab;
        this.unitCurrentPage = 1;
        this.fetchUnit();
    }

    onUnitPageSizeChange(): void {
        this.unitCurrentPage = 1;
        this.fetchUnit();
    }

    get visibleLaborRows(): any[] {
        const rows = this.unitData?.labor_by_provider?.rows || [];
        return this.laborExpanded ? rows : rows.slice(0, 4);
    }

    get visiblePartsRows(): any[] {
        const rows = this.unitData?.refacciones_by_part?.rows || [];
        return this.partsExpanded ? rows : rows.slice(0, 4);
    }

    get visibleProveedores(): any[] {
        const rows = this.unitData?.proveedores || [];
        return this.proveedoresExpanded ? rows : rows.slice(0, 4);
    }

    get proveedoresTotal(): { refacciones_total: number; mano_obra_total: number; total: number } {
        const rows: any[] = this.unitData?.proveedores || [];
        return {
            refacciones_total: rows.reduce((s, r) => s + (r.refacciones_total || 0), 0),
            mano_obra_total: rows.reduce((s, r) => s + (r.mano_obra_total || 0), 0),
            total: rows.reduce((s, r) => s + (r.total || 0), 0),
        };
    }

    spendPct(part: number, total: number): number {
        if (!total) return 0;
        return Math.round((part / total) * 100 * 10) / 10;
    }

    onUnitHistorySearch(): void {
        this.unitCurrentPage = 1;
        this.fetchUnit();
    }

    unitGoToPage(page: number): void {
        if (page < 1 || (this.unitData?.history?.pagination?.last_page && page > this.unitData.history.pagination.last_page)) return;
        this.unitCurrentPage = page;
        this.fetchUnit();
    }

    get unitPageNumbers(): number[] {
        const lastPage = this.unitData?.history?.pagination?.last_page || 1;
        const windowSize = 3;
        const end = Math.min(lastPage, Math.max(1, this.unitCurrentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    expandedRequestId: number | null = null;
    toggleExpanded(id: number): void {
        this.expandedRequestId = this.expandedRequestId === id ? null : id;
    }

    tipoPillClass(priorityName: string | null | undefined): string {
        switch (priorityName) {
            case 'Preventivo': return 'meh-pill--tipo-preventivo';
            case 'Prog. reparación': return 'meh-pill--tipo-prog-reparacion';
            case 'Crítico en ruta': return 'meh-pill--tipo-critico';
            default: return 'meh-pill--tipo-otros';
        }
    }

    /** La tabla "Historial de mantenimiento" usa "Completado" para el estado
     * terminado/pagado (según el diseño), distinto del texto que usa la
     * tabla global — el tono/color es el mismo, solo cambia la etiqueta. */
    historyStatusLabel(tone: string | null | undefined): string {
        switch (tone) {
            case 'terminado':
            case 'pagado':
                return 'Completado';
            case 'cancelado':
                return 'Cancelado';
            case 'en_reparacion':
                return 'En reparación';
            default:
                return 'Pendiente';
        }
    }

    categoryPillClass(category: string | null | undefined): string {
        switch (category) {
            case 'Preventivo': return 'meh-pill--tipo-preventivo';
            case 'Correctivo': return 'meh-pill--tipo-critico';
            case 'Llantas': return 'meh-pill--tipo-llantas';
            default: return 'meh-pill--tipo-otros';
        }
    }
}
